import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RotateCcw, Trash2, Loader2, FileText } from "lucide-react";
import { fetchTrash, restoreDocument, purgeDocument } from "../lib/api";
import { getCategory } from "../lib/categories";
import { Button } from "./ui/button";
import { tr } from "../lib/i18n";

// Shared list of recently deleted documents with Restore / Delete forever (used by the Vault sheet and the Admin card).
export const TrashList = ({ limit, onChanged, compact = false }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["trash"], queryFn: fetchTrash, staleTime: 0 });
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const done = () => { qc.invalidateQueries({ queryKey: ["trash"] }); onChanged?.(); };
  const restore = async (d) => {
    setBusy(d.id);
    try { await restoreDocument(d.id); toast.success(`"${d.title}" is back in the vault`); done(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't restore"); } finally { setBusy(null); }
  };
  const purge = async (d) => {
    setBusy(d.id);
    try { await purgeDocument(d.id); toast.success(`"${d.title}" deleted forever`); setConfirm(null); done(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); } finally { setBusy(null); }
  };
  if (isLoading) return <div className="text-sm text-muted-foreground flex items-center gap-2" data-testid="trash-loading"><Loader2 className="h-4 w-4 animate-spin" /> {tr("Loading…")}</div>;
  const items = limit ? (data?.items || []).slice(0, limit) : data?.items || [];
  if (!items.length) return <div className="text-sm text-muted-foreground" data-testid="trash-empty">{tr("Nothing in recently deleted. Deleted documents stay here for")} {data?.retention_days ?? 30} {tr("days before they're shredded.")}</div>;
  return (
    <ul className={compact ? "space-y-1.5" : "space-y-2"} data-testid="trash-list">
      {items.map((d) => {
        const cat = getCategory(d.category);
        const Icon = cat?.icon || FileText;
        const urgent = d.days_left <= 3;
        return (
          <li key={d.id} className={`rounded-xl border border-border bg-card ${compact ? "px-3 py-2" : "px-3 py-2.5"} flex items-center gap-3`} data-testid={`trash-item-${d.id}`}>
            <span className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${cat.tint}`}><Icon className="h-4 w-4" style={{ color: cat.iconColor }} /></span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid={`trash-title-${d.id}`}>{d.title}</div>
              <div className={`text-xs ${urgent ? "text-destructive" : "text-muted-foreground"}`} data-testid={`trash-days-${d.id}`}>
                {cat?.label || d.category} · {d.days_left === 0 ? "shreds tonight" : `${d.days_left} day${d.days_left === 1 ? "" : "s"} left`}
              </div>
            </div>
            {confirm === d.id ? (
              <div className="flex items-center gap-1.5 shrink-0">
                <Button size="sm" variant="destructive" className="h-7 rounded-full text-xs" data-testid={`trash-purge-confirm-${d.id}`} disabled={busy === d.id} onClick={() => purge(d)}>{busy === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Yes, delete forever"}</Button>
                <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs" data-testid={`trash-purge-cancel-${d.id}`} onClick={() => setConfirm(null)}>{tr("Keep")}</Button>
              </div>
            ) : (
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" className="h-7 rounded-full gap-1 text-xs" data-testid={`trash-restore-${d.id}`} disabled={busy === d.id} onClick={() => restore(d)}>{busy === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} {tr("Restore")}</Button>
                <button type="button" className="h-7 w-7 grid place-items-center rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10" title={tr("Delete forever")} aria-label={tr("Delete forever")} data-testid={`trash-purge-${d.id}`} onClick={() => setConfirm(d.id)}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};
